import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Form, Spinner } from 'react-bootstrap';
import { globalURLS } from '../../../Global/urls.js';

import './TargetSpecialtiesSelector.css';

export type MbbsFilter = 'all' | 'mbbs' | 'non-mbbs';

export interface TargetSpecialty {
    specialtyid: string;
    name: string;
    isNonMBBS?: boolean;
}

export function filterByMbbsFilter<T extends { isNonMBBS?: boolean }>(
    items: T[],
    filter: MbbsFilter,
): T[] {
    if (filter === 'mbbs') return items.filter(item => !item.isNonMBBS);
    if (filter === 'non-mbbs') return items.filter(item => item.isNonMBBS);
    return items;
}

export const SpecialtyLabel: React.FC<{
    name: string;
    isNonMBBS?: boolean;
}> = ({ name, isNonMBBS }) => (
    <span className="specialty-label">
        {name}
        <span
            className={`specialty-mbbs-badge ${isNonMBBS ? 'non-mbbs' : 'mbbs'}`}
        >
            {isNonMBBS ? 'Non-MBBS' : 'MBBS'}
        </span>
    </span>
);

interface Props {
    disabled?: boolean;
    value?: string[];
    onChange: (targetSpecialties: string[]) => void;
    title?: string;
    note?: string | null;
    idPrefix?: string;
}

export function normalizeSpecialties(data: unknown): TargetSpecialty[] {
    if (!Array.isArray(data)) return [];
    const rows: TargetSpecialty[] = [];
    for (const raw of data) {
        if (!raw || typeof raw !== 'object') continue;
        const r = raw as Record<string, unknown>;
        const id =
            r.specialtyid ??
            r.specialtyId ??
            r.SpecialtyId ??
            r.specialty_id ??
            r.id;
        const name = r.name ?? r.Name ?? '';
        const isNonMBBS = Boolean(
            r.isNonMBBS ?? r.isNonMbbs ?? r.nonMBBS ?? r.nonMbbs,
        );
        if (typeof id !== 'string' || !id) continue;
        rows.push({ specialtyid: id, name: String(name), isNonMBBS });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
}

export function normalizeTargetIds(data: unknown): string[] {
    if (!Array.isArray(data)) return [];
    return data
        .map(item => {
            if (typeof item === 'string') return item;
            if (typeof item === 'number') return String(item);
            if (item && typeof item === 'object') {
                const r = item as Record<string, unknown>;
                const id =
                    r.specialtyid ??
                    r.specialtyId ??
                    r.SpecialtyId ??
                    r.specialty_id ??
                    r.id;
                if (typeof id === 'string') return id;
                if (typeof id === 'number') return String(id);
            }
            return '';
        })
        .filter(Boolean);
}

const TargetSpecialtiesSelector: React.FC<Props> = ({
    disabled = false,
    value,
    onChange,
    title = 'Select Target Specialties',
    note = '*Note: Session registrations & attendance will be restricted to chosen specialties*',
    idPrefix = '',
}) => {
    const [expanded, setExpanded] = useState(false);
    const [targetAll, setTargetAll] = useState(true);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [mbbsFilter, setMbbsFilter] = useState<MbbsFilter>('all');
    const [specialties, setSpecialties] = useState<TargetSpecialty[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    useEffect(() => {
        const ids = normalizeTargetIds(value ?? []);
        setTargetAll(ids.length === 0);
        setSelectedIds(new Set(ids));
    }, [value]);

    const notifyChange = useCallback(
        (all: boolean, ids: Set<string>) => {
            onChange(all ? [] : Array.from(ids));
        },
        [onChange],
    );

    const fetchSpecialties = useCallback(async () => {
        setLoading(true);
        setFetchError(false);
        try {
            const resp = await axios.post(
                `${globalURLS.baseURL}getSpecialties`,
                {},
                globalURLS.config,
            );
            const raw =
                resp?.data?.data !== undefined
                    ? resp.data.data
                    : resp?.data;
            setSpecialties(normalizeSpecialties(raw));
        } catch {
            setSpecialties([]);
            setFetchError(true);
        } finally {
            setLoading(false);
            setHasFetched(true);
        }
    }, []);

    useEffect(() => {
        const shouldFetch =
            (expanded || (value?.length ?? 0) > 0) && !hasFetched && !loading;
        if (shouldFetch) {
            fetchSpecialties();
        }
    }, [expanded, value, hasFetched, loading, fetchSpecialties]);

    const filteredSpecialties = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        const byMbbs = filterByMbbsFilter(specialties, mbbsFilter);
        if (!q) return byMbbs;
        return byMbbs.filter(s => s.name.toLowerCase().includes(q));
    }, [specialties, searchTerm, mbbsFilter]);

    const allFilteredSelected =
        filteredSpecialties.length > 0 &&
        filteredSpecialties.every(s => selectedIds.has(s.specialtyid));

    const handleExpand = () => {
        if (disabled) return;
        setExpanded(prev => !prev);
    };

    const handleTargetAllChange = () => {
        const next = !targetAll;
        setTargetAll(next);
        if (next) {
            notifyChange(true, selectedIds);
        }
    };

    const toggleOne = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            if (!targetAll) notifyChange(false, next);
            return next;
        });
    };

    const toggleAllFiltered = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredSpecialties.forEach(s => next.delete(s.specialtyid));
            } else {
                filteredSpecialties.forEach(s => next.add(s.specialtyid));
            }
            if (!targetAll) notifyChange(false, next);
            return next;
        });
    };

    const summary = targetAll
        ? 'All specialties'
        : selectedIds.size === 0
          ? 'No specialties selected'
          : `${selectedIds.size} specialt${selectedIds.size === 1 ? 'y' : 'ies'} selected`;

    return (
        <div className="target-specialties-selector">
            <button
                type="button"
                className="target-specialties-header"
                onClick={handleExpand}
                disabled={disabled}
                aria-expanded={expanded}
            >
                <span className="target-specialties-title">
                    {title}
                </span>
                <span className="target-specialties-summary text-muted">
                    {summary}
                </span>
                <span className="target-specialties-chevron">
                    {expanded ? '▾' : '▸'}
                </span>
            </button>
            {note ? (
                <p className="target-specialties-note mb-0">{note}</p>
            ) : null}

            {expanded && (
                <div className="target-specialties-body border rounded p-3 mt-3 bg-white">
                    <Form.Check
                        type="switch"
                        id={`${idPrefix}target-specialties-all`}
                        label="All"
                        checked={targetAll}
                        disabled={disabled}
                        onChange={handleTargetAllChange}
                        className="mb-3"
                    />

                    {!targetAll && (
                        <>
                            <Form.Control
                                type="search"
                                placeholder="Search specialties"
                                value={searchTerm}
                                disabled={disabled}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="mb-3"
                                aria-label="Search specialties"
                            />
                            <Form.Select
                                value={mbbsFilter}
                                disabled={disabled}
                                onChange={e =>
                                    setMbbsFilter(e.target.value as MbbsFilter)
                                }
                                className="mb-3"
                                aria-label="Filter by MBBS type"
                            >
                                <option value="all">All specialties</option>
                                <option value="mbbs">MBBS only</option>
                                <option value="non-mbbs">Non-MBBS only</option>
                            </Form.Select>

                            {loading ? (
                                <div className="d-flex justify-content-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : fetchError ? (
                                <p className="text-danger mb-0">
                                    Failed to load specialties.
                                </p>
                            ) : (
                                <div className="target-specialties-list">
                                    {filteredSpecialties.length > 0 && (
                                        <Form.Check
                                            type="checkbox"
                                            id={`${idPrefix}target-specialties-select-all`}
                                            label="Select all"
                                            checked={allFilteredSelected}
                                            disabled={disabled}
                                            onChange={toggleAllFiltered}
                                            className="mb-2 fw-semibold"
                                        />
                                    )}
                                    {filteredSpecialties.map(spec => (
                                        <Form.Check
                                            key={spec.specialtyid}
                                            type="checkbox"
                                            id={`${idPrefix}target-spec-${spec.specialtyid}`}
                                            label={
                                                <SpecialtyLabel
                                                    name={spec.name}
                                                    isNonMBBS={
                                                        spec.isNonMBBS
                                                    }
                                                />
                                            }
                                            checked={selectedIds.has(
                                                spec.specialtyid,
                                            )}
                                            disabled={disabled}
                                            onChange={() =>
                                                toggleOne(spec.specialtyid)
                                            }
                                            className="mb-1"
                                        />
                                    ))}
                                    {filteredSpecialties.length === 0 && (
                                        <p className="text-muted mb-0">
                                            No specialties found.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default TargetSpecialtiesSelector;
